import React, { useState, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface InvestigatorAvatarProps {
  avatar: string | undefined;
  onAvatarChange: (avatarDataUrl: string) => void;
}

const InvestigatorAvatar: React.FC<InvestigatorAvatarProps> = ({
  avatar,
  onAvatarChange
}) => {
  const { t } = useTranslation("widgets");
  const [isHovering, setIsHovering] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      toast.error(t("avatar.errorImage"));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("avatar.errorSize"));
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      if (event.target?.result) {
        onAvatarChange(event.target.result as string);
      }
    };
    reader.onerror = () => {
      toast.error(t("avatar.errorRead"));
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col items-center mt-8">
      <div
        className="relative w-[200px] h-[200px] rounded-full overflow-hidden cursor-pointer"
        onClick={triggerFileInput}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <Avatar className="w-full h-full border-4 border-brand-cream">
          <AvatarImage src={avatar} alt={t("avatar.alt")} className="object-cover w-full h-full" />
          <AvatarFallback className="bg-card text-brand-cream text-4xl flex items-center justify-center">
            <Camera size={64} />
          </AvatarFallback>
        </Avatar>

        {isHovering && (
          <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
            <span className="text-white ml-2 font-medium">{t("avatar.uploadHover")}</span>
          </div>
        )}
      </div>

      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />

      <div className="flex flex-col items-center mt-3">
        <p className="text-neutral-100 text-sm">{t("avatar.uploadHelp")}</p>
      </div>
    </div>
  );
};

export default InvestigatorAvatar;
